import { CloudUpload, Loader2, WifiOff } from 'lucide-react'
import { useOutboxStore } from '@/lib/offline/outbox'
import { cn } from '@/lib/utils'

/**
 * Thin strip shown while the device is offline or has changes waiting to
 * upload. Sits under the header so field users always know whether what
 * they just typed has reached the server.
 */
export function OfflineBanner({ className }: { className?: string }) {
  const online = useOutboxStore((s) => s.online)
  const syncing = useOutboxStore((s) => s.syncing)
  const flush = useOutboxStore((s) => s.flush)
  const pending = useOutboxStore((s) => s.entries.length)
  const failing = useOutboxStore((s) => s.entries.some((e) => e.attempts > 0))

  if (online && pending === 0) return null

  const plural = pending === 1 ? 'change' : 'changes'
  let text: string
  if (!online) {
    text = pending > 0
      ? `No connection. ${pending} ${plural} saved on this device and will upload when you're back online.`
      : "No connection. Anything you type is saved on this device and will upload when you're back online."
  } else if (syncing) {
    text = `Uploading ${pending} saved ${plural}...`
  } else if (failing) {
    text = `Can't reach the server. ${pending} ${plural} saved on this device — will keep retrying.`
  } else {
    text = `${pending} saved ${plural} waiting to upload.`
  }

  return (
    <div
      role="status"
      className={cn(
        'flex items-center gap-2 border-b px-4 py-2 text-[13px]',
        online ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-zinc-300 bg-zinc-100 text-zinc-800',
        className,
      )}
    >
      {syncing
        ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        : online
          ? <CloudUpload className="h-4 w-4 shrink-0" />
          : <WifiOff className="h-4 w-4 shrink-0" />}
      <span className="min-w-0 flex-1">{text}</span>
      {online && pending > 0 && !syncing && (
        <button
          type="button"
          onClick={() => { void flush() }}
          className="shrink-0 rounded border border-amber-300 px-2 py-0.5 text-xs font-medium hover:bg-amber-100"
        >
          Retry
        </button>
      )}
    </div>
  )
}
